import { useEffect } from 'react';

/**
 * Set <meta name="description"> and Open Graph tags for the current page.
 * Lets public pages give crawlers + social shares real metadata instead of
 * the generic landing-page description from index.html.
 *
 * Restores the previous values on unmount so back-navigation stays clean.
 */
export function useMetaTags({ description, ogTitle, ogDescription, ogType = 'article', canonical, robots, jsonLd }) {
  // jsonLd is compared by content, not identity, so callers can pass a fresh
  // object literal each render without re-running the effect.
  const jsonLdText = jsonLd ? JSON.stringify(jsonLd) : null;
  useEffect(() => {
    const restorers = [];

    if (description) {
      restorers.push(setMeta('name', 'description', description));
    }
    if (ogTitle) {
      restorers.push(setMeta('property', 'og:title', ogTitle));
    }
    if (ogDescription) {
      restorers.push(setMeta('property', 'og:description', ogDescription));
    }
    if (ogType) {
      restorers.push(setMeta('property', 'og:type', ogType));
    }
    if (canonical) {
      restorers.push(setLink('canonical', canonical));
    }
    if (robots) {
      restorers.push(setMeta('name', 'robots', robots));
    }
    if (jsonLdText) {
      restorers.push(addJsonLd(jsonLdText));
    }

    return () => restorers.forEach((fn) => fn());
  }, [description, ogTitle, ogDescription, ogType, canonical, robots, jsonLdText]);
}

function setMeta(attr, value, content) {
  let el = document.head.querySelector(`meta[${attr}="${value}"]`);
  const existed = !!el;
  const prev = el?.getAttribute('content');
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return () => {
    if (existed && prev != null) el.setAttribute('content', prev);
    else el?.remove();
  };
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const existed = !!el;
  const prev = el?.getAttribute('href');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return () => {
    if (existed && prev != null) el.setAttribute('href', prev);
    else el?.remove();
  };
}

// Page-scoped structured data. Appended alongside (not replacing) the
// site-wide SoftwareApplication block in index.html; removed on unmount so
// an Article schema never lingers onto the next route.
function addJsonLd(text) {
  // A prerendered snapshot already carries this element; reuse it, otherwise
  // the page ends up with two Article blocks after React mounts.
  let el = document.head.querySelector('script[data-page-jsonld]');
  const existed = !!el;
  const prev = el?.textContent;
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-page-jsonld', '');
    document.head.appendChild(el);
  }
  el.textContent = text;
  return () => {
    if (existed && prev != null) el.textContent = prev;
    else el.remove();
  };
}
